import { useState } from 'react';
import { clsx } from 'clsx';
import {
  CheckCircle2,
  XCircle,
  Merge,
  Loader2,
  GitBranch,
  Network,
  Tags,
} from 'lucide-react';
import { useCatalogsStore } from '@/stores/catalogs';
import type { ProposalSource, ProposedCanonical } from '@/services/api';

const SOURCE_META: Record<
  ProposalSource,
  { label: string; Icon: React.ElementType; color: string }
> = {
  uos: {
    label: 'UOS',
    Icon: GitBranch,
    color: 'bg-violet-50 text-violet-700 border-violet-200',
  },
  concept_type: {
    label: 'ConceptType',
    Icon: Network,
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  ad_taxonomy: {
    label: 'Ad Taxonomy',
    Icon: Tags,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  concept_type_axis: {
    label: 'Layer 1 Axes',
    Icon: Network,
    color: 'bg-sky-50 text-sky-700 border-sky-200',
  },
};

function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function ProposedCanonicalsQueueList() {
  const { queue, selectedProposal, queueLoading, selectProposal } = useCatalogsStore();

  if (queueLoading && queue.length === 0) {
    return (
      <div className="text-sm text-slate-400 text-center py-8">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading proposals...
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="text-sm text-slate-400 text-center py-12">
        <CheckCircle2 size={24} className="text-emerald-400 mx-auto mb-2" />
        <p>Inbox zero — no pending catalog proposals.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
      {queue.map((p) => {
        const meta = SOURCE_META[p.source_type];
        const Icon = meta.Icon;
        const selected = selectedProposal?.id === p.id;
        return (
          <button
            key={p.id}
            onClick={() => selectProposal(p)}
            className={clsx(
              'w-full text-left flex items-center gap-3 px-4 py-3 transition-colors',
              selected
                ? 'bg-brand-teal/5 border-l-4 border-l-brand-teal'
                : 'hover:bg-slate-50 border-l-4 border-l-transparent',
            )}
          >
            <span
              className={clsx(
                'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border flex items-center gap-1 flex-shrink-0',
                meta.color,
              )}
            >
              <Icon size={10} />
              {meta.label}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">
                {p.display_name}
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                {p.category && (
                  <span className="truncate max-w-[10rem]">{p.category}</span>
                )}
                {p.proposing_agent && <span>· {p.proposing_agent}</span>}
                {p.proposal_count != null && (
                  <span>· seen {p.proposal_count}×</span>
                )}
              </div>
            </div>
            {p.confidence != null && (
              <span className="text-[10px] text-emerald-600 font-semibold flex-shrink-0">
                {(p.confidence * 100).toFixed(0)}%
              </span>
            )}
            <span className="text-[10px] text-slate-400 flex-shrink-0">
              {timeAgo(p.first_seen_at)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const UOS_CLASS_OPTIONS = [
  'terminal_value',
  'instrumental_value',
  'functional_need',
  'emotional_need',
  'social_need',
  'purchase_scenario',
  'life_stage_context',
  'market_concept',
  'brand_attribute',
  'product_attribute',
  'consumer_archetype',
  'self_concept_signal',
  'friction_factor',
  'decision_trigger',
];

function formatLabel(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function ProposedReviewPanel() {
  const { selectedProposal: p, actingId, actOnProposal } = useCatalogsStore();

  const [mergeTarget, setMergeTarget] = useState('');
  const [note, setNote] = useState('');

  if (!p) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400">
        Select a proposal to review
      </div>
    );
  }

  const busy = actingId === p.id;
  const isUOS = p.source_type === 'uos';
  const isAdTaxonomy = p.source_type === 'ad_taxonomy';
  const isConceptType = p.source_type === 'concept_type';
  const meta = SOURCE_META[p.source_type];
  const Icon = meta.Icon;

  const handleApprove = () =>
    actOnProposal(p, 'approve', { reviewed_by: 'admin-ui', human_note: note || undefined });

  const handleReject = () => {
    if (isUOS && !mergeTarget) return; // UOS rejects need a target class
    actOnProposal(p, 'reject', {
      reviewed_by: 'admin-ui',
      reclassify_to: isUOS ? mergeTarget : undefined,
      human_note: note || undefined,
    });
  };

  const handleMergeInto = () => {
    if (!mergeTarget) return;
    actOnProposal(p, 'merge_into', {
      merge_into_id: mergeTarget,
      reviewed_by: 'admin-ui',
      human_note: note || undefined,
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2 mb-1">
          <span
            className={clsx(
              'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border flex items-center gap-1',
              meta.color,
            )}
          >
            <Icon size={10} />
            {meta.label}
          </span>
          {p.confidence != null && (
            <span className="text-[10px] text-emerald-600 font-semibold">
              confidence {(p.confidence * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <h2 className="text-lg font-semibold text-navy-900">{p.display_name}</h2>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
          <code>{p.source_id}</code>
          {p.category && <span>{p.category}</span>}
          {p.proposing_agent && <span>by {p.proposing_agent}</span>}
          {p.proposal_count != null && <span>seen {p.proposal_count}×</span>}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
        {p.description && (
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Description
            </div>
            <p className="text-sm text-slate-700">{p.description}</p>
          </div>
        )}

        {p.extra && Object.keys(p.extra).length > 0 && (
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
              Extra
            </div>
            <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 overflow-auto">
              {JSON.stringify(p.extra, null, 2)}
            </pre>
          </div>
        )}

        <div>
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
            Note (optional)
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a reviewer note (stored for audit)"
            rows={2}
            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
          />
        </div>
      </div>

      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 space-y-2">
        {/* Merge-into target selector */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
            {isUOS ? 'Reclassify into existing UOS class' : 'Merge into canonical (id)'}
          </label>
          {isUOS ? (
            <select
              value={mergeTarget}
              onChange={(e) => setMergeTarget(e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              <option value="">Select UOS class…</option>
              {UOS_CLASS_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {formatLabel(k)}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={mergeTarget}
              onChange={(e) => setMergeTarget(e.target.value)}
              placeholder="canonical entity id to absorb this proposal"
              disabled={isAdTaxonomy}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal disabled:bg-slate-100 disabled:text-slate-400"
            />
          )}
          {isAdTaxonomy && (
            <p className="text-[10px] text-slate-400 italic">
              Merge-into for Ad Taxonomy is deferred to Phase 4 (registry lacks a
              persistent merge log).
            </p>
          )}
          {isConceptType && (
            <p className="text-[10px] text-slate-400 italic">
              Merging absorbs this concept as a source of the target canonical entity.
            </p>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={handleApprove}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
            Approve
          </button>
          <button
            onClick={handleReject}
            disabled={busy || (isUOS && !mergeTarget)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
          >
            <XCircle size={11} />
            Reject
          </button>
          <button
            onClick={handleMergeInto}
            disabled={busy || !mergeTarget || isAdTaxonomy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
          >
            <Merge size={11} />
            Merge Into
          </button>
        </div>
      </div>
    </div>
  );
}
